import { nanoid } from 'nanoid';
import type { Workflow, FlowNode, FlowEdge, TriggerData, WaitData, ConditionData, ActionData, WaitUnit, TriggerSource, ActionType, FlowData } from '../types';

const WORKER_BASE = 'https://workflow-ai.jalen1wa.workers.dev';

export interface AiModel {
  id: string;
  label: string;
  desc: string;
}

let cachedModels: AiModel[] | null = null;

export async function fetchModels(): Promise<AiModel[]> {
  if (cachedModels) return cachedModels;
  try {
    const r = await fetch(`${WORKER_BASE}/api/models`);
    const j = await r.json();
    cachedModels = j.models;
    return j.models;
  } catch {
    cachedModels = [
      { id: '@cf/meta/llama-3.1-8b-instruct-fast', label: 'Llama 3.1 8B', desc: 'Fast + reliable default' },
    ];
    return cachedModels;
  }
}

/* ============================================================ */
/* ===== REAL AI VIA CLOUDFLARE WORKERS AI ==================== */
/* ============================================================ */

export interface AiBuildResult {
  workflow: Workflow;
  source: 'ai' | 'local';
  model?: string;
}

export async function buildWithAI(prompt: string, model: string): Promise<AiBuildResult> {
  try {
    const r = await fetch(`${WORKER_BASE}/api/build`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt, model }),
    });
    const j = await r.json();
    if (!j.ok || !j.workflow) {
      console.warn('AI build failed, falling back to local parser:', j.error);
      return { workflow: parseToWorkflow(prompt), source: 'local' };
    }
    const wf = normalizeWorkflow(j.workflow, prompt);
    return { workflow: wf, source: 'ai', model: j.model };
  } catch (err) {
    console.warn('AI build error, falling back to local parser:', err);
    return { workflow: parseToWorkflow(prompt), source: 'local' };
  }
}

export async function runAiSmart(prompt: string, model: string, format: 'text' | 'json' = 'text', context?: Record<string, unknown>): Promise<string> {
  const r = await fetch(`${WORKER_BASE}/api/smart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, model, format, context }),
  });
  const j = await r.json();
  if (!j.ok) throw new Error(j.error || 'AI smart run failed');
  return j.output;
}

/* ============================================================ */
/* ===== NORMALIZE AI OUTPUT ================================== */
/* ============================================================ */

const VALID_TRIGGER_SOURCES = new Set<TriggerSource>([
  'call.missed', 'call.completed', 'sms.received', 'job.status_changed',
  'invoice.aged', 'storm.hail_cell', 'lead.created', 'schedule.cron',
]);
const VALID_ACTION_TYPES = new Set<ActionType>([
  'sms.send', 'email.send', 'task.create', 'workflow.notify_rep', 'webhook.post', 'log.audit',
]);
const VALID_WAIT_UNITS = new Set<WaitUnit>(['seconds', 'minutes', 'hours', 'days']);
const FIELD_ALIASES: Record<string, string> = {
  'invoice.balance': 'invoice.balance_due',
  'customer.last_serviced': 'customer.last_serviced_months',
  'customer.optin': 'customer.sms_opt_in',
  'storm.size': 'storm.cell_size_in',
};

function normalizeWorkflow(raw: any, originalPrompt: string): Workflow {
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];
  const idMap = new Map<string, string>();

  const rawNodes = Array.isArray(raw.nodes) ? raw.nodes : [];
  const rawEdges = Array.isArray(raw.edges) ? raw.edges : [];

  // Layout: stack vertically
  rawNodes.forEach((rn: any, i: number) => {
    const oldId = String(rn.id || `n${i}`);
    const newId = nanoid(8);
    idMap.set(oldId, newId);

    const data = normalizeNodeData(rn.data, rn.type);
    if (!data) return;

    nodes.push({
      id: newId,
      type: data.kind === 'ai_smart' ? 'ai_smart' : data.kind,
      position: { x: 60 + (i % 2) * 320, y: 60 + Math.floor(i / 2) * 200 },
      data,
    });
  });

  rawEdges.forEach((re: any) => {
    const src = idMap.get(String(re.source));
    const tgt = idMap.get(String(re.target));
    if (!src || !tgt) return;
    edges.push({
      id: `e-${src}-${tgt}-${nanoid(4)}`,
      source: src,
      target: tgt,
      sourceHandle: re.sourceHandle,
      label: re.label,
      type: 'smoothstep',
      style: { stroke: '#C3A35B', strokeWidth: 2 },
    });
  });

  // Tidy positions: trigger at top, then BFS
  layoutBFS(nodes, edges);

  return {
    id: 'wf-' + nanoid(8),
    name: String(raw.name || 'AI-generated workflow').slice(0, 60),
    description: String(raw.description || originalPrompt).slice(0, 200),
    active: false,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    version: 1,
    nodes,
    edges,
  };
}

function normalizeNodeData(d: any, fallbackType?: string): FlowData | null {
  if (!d || typeof d !== 'object') return null;
  const kind = d.kind || fallbackType;

  if (kind === 'trigger') {
    let source = d.source as TriggerSource;
    if (!VALID_TRIGGER_SOURCES.has(source)) source = 'call.missed';
    return {
      kind: 'trigger',
      source,
      label: String(d.label || 'Trigger'),
      description: d.description ? String(d.description) : undefined,
    };
  }
  if (kind === 'wait') {
    let unit = d.unit as WaitUnit;
    if (!VALID_WAIT_UNITS.has(unit)) unit = 'minutes';
    const amount = typeof d.amount === 'number' ? d.amount : parseInt(d.amount, 10) || 1;
    return {
      kind: 'wait',
      label: String(d.label || `Wait ${amount} ${unit}`),
      amount,
      unit,
      description: d.description ? String(d.description) : undefined,
    };
  }
  if (kind === 'condition') {
    let field = String(d.field || 'customer.last_serviced_months');
    if (FIELD_ALIASES[field]) field = FIELD_ALIASES[field];
    return {
      kind: 'condition',
      label: String(d.label || 'Condition'),
      field: field as ConditionData['field'],
      operator: (d.operator || 'equals') as ConditionData['operator'],
      value: String(d.value ?? ''),
      description: d.description ? String(d.description) : undefined,
    };
  }
  if (kind === 'action') {
    let type = d.type as ActionType;
    if (!VALID_ACTION_TYPES.has(type)) type = 'sms.send';
    return {
      kind: 'action',
      label: String(d.label || 'Action'),
      type,
      to: d.to ? String(d.to) : undefined,
      body: d.body ? String(d.body) : undefined,
      description: d.description ? String(d.description) : undefined,
    };
  }
  if (kind === 'ai_smart') {
    return {
      kind: 'ai_smart',
      label: String(d.label || 'AI step'),
      model: String(d.model || '@cf/meta/llama-3.1-8b-instruct-fast'),
      prompt: String(d.prompt || ''),
      format: (d.format === 'json' ? 'json' : 'text'),
      storeAs: d.storeAs ? String(d.storeAs) : undefined,
      description: d.description ? String(d.description) : undefined,
    };
  }
  return null;
}

function layoutBFS(nodes: FlowNode[], edges: FlowEdge[]) {
  const STEP_X = 280;
  const STEP_Y = 200;
  const triggers = nodes.filter(n => n.data.kind === 'trigger');
  if (triggers.length === 0) return;

  const visited = new Set<string>();
  const queue: { id: string; depth: number; lane: number }[] = triggers.map((t, i) => ({ id: t.id, depth: 0, lane: i }));
  const depths = new Map<string, number>();
  const lanes = new Map<string, number>();

  while (queue.length) {
    const { id, depth, lane } = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    depths.set(id, Math.max(depths.get(id) || 0, depth));
    lanes.set(id, lane);

    const outgoing = edges.filter(e => e.source === id);
    outgoing.forEach((e, i) => {
      if (!visited.has(e.target)) {
        const isNoMatch = e.sourceHandle === 'nomatch' || (typeof e.label === 'string' && /no\s*match/i.test(e.label));
        const newLane = outgoing.length > 1 ? (isNoMatch ? lane + 1 : lane) : lane;
        queue.push({ id: e.target, depth: depth + 1, lane: newLane });
      }
    });
  }

  nodes.forEach(n => {
    const d = depths.get(n.id) ?? 0;
    const l = lanes.get(n.id) ?? 0;
    n.position = { x: 60 + l * STEP_X, y: 60 + d * STEP_Y };
  });
}

/* ============================================================ */
/* ===== LOCAL FALLBACK PARSER (when worker unreachable) ===== */
/* ============================================================ */

interface ParsedDuration { amount: number; unit: WaitUnit; }

function parseDuration(text: string): ParsedDuration | null {
  const m = text.match(/(\d+)\s*(second|minute|hour|day|week|month)s?/i);
  if (m) {
    let amount = parseInt(m[1], 10);
    let unit: WaitUnit = m[2].toLowerCase().startsWith('s') ? 'seconds'
                      : m[2].toLowerCase().startsWith('m') && m[2].toLowerCase().includes('min') ? 'minutes'
                      : m[2].toLowerCase().startsWith('h') ? 'hours'
                      : 'days';
    if (m[2].toLowerCase().startsWith('w')) amount = amount * 7;
    if (m[2].toLowerCase().startsWith('mon')) amount = amount * 30;
    return { amount, unit };
  }
  return null;
}

interface Intent {
  trigger?: { source: TriggerSource; label: string; description: string };
  condition?: { field: ConditionData['field']; operator: ConditionData['operator']; value: string; label: string };
  wait?: { amount: number; unit: WaitUnit; label: string };
  action?: { type: ActionType; label: string; body: string; to: string };
}

function detectIntent(input: string): Intent {
  const t = input.toLowerCase();
  const intent: Intent = {};
  if (/storm|hail|wind|weather/.test(t)) {
    const sizeMatch = t.match(/([0-9.]+)\s*"\s*(?:hail|inch)/);
    const size = sizeMatch ? sizeMatch[1] : '1.5';
    intent.trigger = { source: 'storm.hail_cell', label: `Hail cell ≥ ${size}" in customer zip`, description: 'NWS hail polygon feed via Storm Command' };
  } else if (/miss.+call|missed call|voicemail/.test(t)) {
    intent.trigger = { source: 'call.missed', label: 'Inbound call missed', description: 'Caller rings ≥ 4 times with no answer' };
  } else if (/invoice|overdue|unpaid|payment.*reminder|aged/.test(t)) {
    intent.trigger = { source: 'invoice.aged', label: 'Invoice aged · unpaid', description: 'Nightly check at 8 AM' };
  } else if (/new lead|lead created|web form/.test(t)) {
    intent.trigger = { source: 'lead.created', label: 'New lead created', description: 'From web form, manual entry, or storm tag' };
  } else if (/text|sms received|customer text/.test(t)) {
    intent.trigger = { source: 'sms.received', label: 'SMS received', description: 'Two-way SMS via Twilio' };
  } else if (/job status|inspection.*(done|complete)|status change/.test(t)) {
    intent.trigger = { source: 'job.status_changed', label: 'Job status changed', description: 'Tech updates job status' };
  } else if (/every|daily|weekly|scheduled|cron/.test(t)) {
    intent.trigger = { source: 'schedule.cron', label: 'Scheduled run', description: 'Cron schedule' };
  } else {
    intent.trigger = { source: 'call.missed', label: 'Inbound event', description: 'Pick a source in properties' };
  }
  if (/opt.?in|opted in/.test(t)) intent.condition = { field: 'customer.sms_opt_in', operator: 'equals', value: 'true', label: 'Customer opted in to SMS' };
  else if (/dormant|haven't|past customer|last serviced/.test(t)) {
    const m = t.match(/(\d+)\s*(?:month|year)/);
    const months = m ? parseInt(m[1], 10) * (t.includes('year') ? 12 : 1) : 12;
    intent.condition = { field: 'customer.last_serviced_months', operator: 'gte', value: String(months), label: `Dormant ≥ ${months} months` };
  } else if (/balance|over.*\$|owing/.test(t)) {
    const m = t.match(/\$\s*(\d+)/);
    const amt = m ? m[1] : '50';
    intent.condition = { field: 'invoice.balance_due', operator: 'gt', value: amt, label: `Balance > $${amt}` };
  }
  const dur = parseDuration(input);
  if (dur && /wait|delay|after|before/.test(t)) intent.wait = { ...dur, label: `Wait ${dur.amount} ${dur.unit}` };
  if (/email/.test(t)) intent.action = { type: 'email.send', label: 'Send email', to: '{{customer.email}}', body: 'Hey {{customer.first_name}} — quick note.' };
  else if (/review/.test(t)) intent.action = { type: 'sms.send', label: 'Review request', to: '{{customer.phone}}', body: 'Thanks for trusting us! Mind dropping a Google review? {{review_link}}' };
  else if (/payment.*link|stripe|pay.*now/.test(t)) intent.action = { type: 'sms.send', label: 'Stripe payment link', to: '{{customer.phone}}', body: 'INV-{{invoice.number}} · ${{invoice.balance}} · pay here: {{stripe_link}}' };
  else if (/storm.*sms|inspection.*link|free.*inspection/.test(t)) intent.action = { type: 'sms.send', label: 'Storm SMS', to: '{{customer.phone}}', body: 'Heavy hail in your zip last night. Free inspection: {{schedule_link}}' };
  else if (/task|todo/.test(t)) intent.action = { type: 'task.create', label: 'Follow-up task', to: 'auto-route', body: 'Manual follow-up' };
  else intent.action = { type: 'sms.send', label: 'Send SMS', to: '{{customer.phone}}', body: 'Hey {{customer.first_name}} — quick note from the team.' };
  return intent;
}

function mkEdge(source: string, target: string, label?: string, sourceHandle?: string): FlowEdge {
  return {
    id: `e-${source}-${target}`,
    source, target, label, sourceHandle,
    type: 'smoothstep',
    style: { stroke: '#C3A35B', strokeWidth: 2 },
  };
}

export function parseToWorkflow(input: string): Workflow {
  const intent = detectIntent(input);
  const nodes: FlowNode[] = [];
  const edges: FlowEdge[] = [];
  let y = 60;
  const x = 60;
  const STEP_Y = 200;
  const trig: FlowNode = { id: nanoid(8), type: 'trigger', position: { x, y }, data: { kind: 'trigger', ...intent.trigger! } as TriggerData };
  nodes.push(trig);
  y += STEP_Y;
  let prev = trig.id;
  let prevHandle: string | undefined;
  if (intent.condition) {
    const cond: FlowNode = { id: nanoid(8), type: 'condition', position: { x, y }, data: { kind: 'condition', ...intent.condition } as ConditionData };
    nodes.push(cond);
    edges.push(mkEdge(prev, cond.id));
    const skip: FlowNode = { id: nanoid(8), type: 'action', position: { x: x + 320, y }, data: { kind: 'action', type: 'log.audit', label: 'Skip · log to audit', body: 'No match · compliance log only' } as ActionData };
    nodes.push(skip);
    edges.push(mkEdge(cond.id, skip.id, 'no match', 'nomatch'));
    prev = cond.id; prevHandle = 'match';
    y += STEP_Y;
  }
  if (intent.wait) {
    const wait: FlowNode = { id: nanoid(8), type: 'wait', position: { x, y }, data: { kind: 'wait', ...intent.wait } as WaitData };
    nodes.push(wait);
    edges.push(mkEdge(prev, wait.id, prevHandle === 'match' ? 'match' : undefined, prevHandle));
    prev = wait.id; prevHandle = undefined;
    y += STEP_Y;
  }
  const act: FlowNode = { id: nanoid(8), type: 'action', position: { x, y }, data: { kind: 'action', ...intent.action! } as ActionData };
  nodes.push(act);
  edges.push(mkEdge(prev, act.id, prevHandle === 'match' ? 'match' : undefined, prevHandle));
  const triggerLabel = intent.trigger?.label || 'Workflow';
  const actionLabel = intent.action?.label || 'Action';
  const name = `${triggerLabel.replace(/[≥<>"]/g, '').slice(0, 30)} → ${actionLabel.slice(0, 22)}`;
  return {
    id: 'wf-' + nanoid(8),
    name, description: `Local fallback parse: "${input.slice(0, 80)}"`,
    active: false, createdAt: Date.now(), updatedAt: Date.now(), version: 1,
    nodes, edges,
  };
}

export const SAMPLE_PROMPTS = [
  "When a hail cell > 2 inches hits a customer's zip, send the dormant ones a free inspection link",
  "After a job is paid in full, wait 2 days then text a Google review request",
  "When an invoice is 7 days overdue with balance over $50, SMS a Stripe payment link",
  "When we miss a call, wait 30 seconds and text the schedule link",
  "Every 90 days find past customers not serviced in 6 months and send a check-in",
  "When a web form is submitted, notify the closest rep and email a welcome",
];
